import { useClerk, useUser } from '@clerk/clerk-react';
import { Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';

const FULLSCREEN_PATHS = ['/leads/new'];

function isFullscreenPath(pathname: string): boolean {
  return FULLSCREEN_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}
import {
  BadgeEuro,
  Building2,
  Download,
  Handshake,
  LayoutDashboard,
  LogOut,
  MessageCircleQuestion,
  Plus,
  RefreshCcw,
  Settings,
  Shield,
  Trophy,
  UserCog,
  Users,
  Sparkles,
  Store as StoreIcon,
  Wallet,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
  isRetail,
  isStoreAssignableRole,
  type MyFinanceAccess,
  type Partner,
  type User,
  type UserRole,
} from '@doorly/shared';
import { cn } from '../lib/cn';
import { isImpersonating } from '../lib/impersonation';
import { clearActingAs, isActingAs } from '../lib/actingAs';
import { SwitchUserModal } from '../features/pin/SwitchUserModal';
import { useLiveQuery } from 'dexie-react-hooks';
import { db } from '../db/schema';
import { useApiQuery } from '../lib/api';
import { checkForUpdates } from '../lib/updateCheck';
import { Avatar, initialsFrom } from './ui/avatar';
import { DoorlyLogo } from './DoorlyLogo';
import {
  InstallAppButton,
  InstallPWAPrompt,
  canInstallNatively,
  triggerInstall,
} from './InstallPWAPrompt';
import { identifyAnalytics } from '../lib/analytics';
import { AppVersion } from './AppVersion';
import { PendingConsentBell } from './PendingConsentBell';
import { storiesEnabledFor, useFeatureFlags } from '../lib/featureFlags';
import { useStoriesCount, useUnseenStoriesCount } from '../features/stories/useStories';
import { SyncStatusBadge } from './SyncDrawer';
import { TestModeBanner } from './TestModeBanner';
import { ImpersonationBanner } from './ImpersonationBanner';
import { ImpersonationPicker } from './ImpersonationPicker';
import { toast } from './Toaster';
import { useFinanceAccess } from '../features/finance/useFinanceAccess';
import { StoreBanner } from '../features/stores/StoreBanner';

function handleSignOut(
  signOut: (opts?: { redirectUrl?: string }) => Promise<void>,
  setLoggingOut: (v: boolean) => void,
): void {
  db.mutations
    .where('status')
    .anyOf('queued', 'in_flight', 'failed')
    .count()
    .then((pending) => {
      if (pending > 0) {
        const confirmed = confirm(
          `${pending} modification${pending > 1 ? 's sont' : ' est'} en attente de synchronisation. ` +
            `Forcer la déconnexion maintenant les supprimera définitivement. Continuer ?`,
        );
        if (!confirmed) return;
      }
      setLoggingOut(true);
      // Small delay so the splash renders before navigation
      setTimeout(() => {
        window.location.href = '/logout';
      }, 100);
    })
    .catch(() => {
      setLoggingOut(true);
      setTimeout(() => {
        window.location.href = '/logout';
      }, 100);
    });
}

// ── Nav config ──────────────────────────────────────────────────────

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  roles?: UserRole[];
  /** Ouvre aussi l'entrée au RP d'un partner `isAggregator` (cf. visibleSidebar). */
  aggregatorOnly?: boolean;
  /**
   * Entrée rémunération d'un non-admin : masquée tant que
   * `features.financeVisibility` ne vaut pas `everyone` (l'API refuse de
   * toute façon : 403 sur `/finance/*`).
   */
  financeOpenOnly?: boolean;
  /**
   * Entrée rémunération ouverte selon `GET /finance/access` (drapeau ET
   * réglages du RP du réseau). Jamais en session PIN (accès tout fermé).
   */
  financeGate?: (access: MyFinanceAccess) => boolean;
  /** Réseau de boutiques seulement (l'admin la voit toujours). */
  retailOnly?: boolean;
}

const SIDEBAR_NAV: NavItem[] = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/leads', label: 'Fiches', icon: Handshake },
  {
    to: '/tickets',
    label: 'Tickets',
    icon: MessageCircleQuestion,
    roles: ['admin'],
  },
  {
    to: '/users',
    label: 'Utilisateurs',
    icon: UserCog,
    roles: ['admin', 'responsable_partenaire', 'responsable_backoffice', 'manager', 'superviseur'],
  },
  {
    // Magasins (réunion du 09/10/2026) : page dédiée plutôt qu'une section de
    // Configuration, que le back-office n'a pas.
    to: '/magasins',
    label: 'Magasins',
    icon: StoreIcon,
    roles: ['admin', 'responsable_partenaire', 'responsable_backoffice'],
    retailOnly: true,
  },
  {
    to: '/partners',
    label: 'Partenaires',
    icon: Building2,
    roles: ['admin'],
    // …et le RP d'un réseau agrégateur, qui y crée les réseaux de ses
    // filiales. Le droit ne se déduit pas du rôle (l'agrégation est portée
    // par le partner) : `aggregatorOnly` ouvre l'entrée sur la donnée.
    aggregatorOnly: true,
  },
  // Rémunération : tableau de bord Finance (admin) ; « Ma rémunération »
  // (rétrocession de SON réseau) pour le RP, derrière le drapeau.
  { to: '/finance', label: 'Finance', icon: Wallet, roles: ['admin'] },
  {
    to: '/ma-remuneration',
    label: 'Ma rémunération',
    icon: Wallet,
    // Back-office : seulement si son RP lui a ouvert la rétrocession.
    roles: ['responsable_partenaire', 'responsable_backoffice'],
    financeOpenOnly: true,
    financeGate: (a) => a.payout !== 'none',
  },
  // Commissions vendeurs (lot 5) : réseau (RP, back-office), équipe
  // (manager, superviseur) — selon les réglages du RP.
  {
    to: '/commissions',
    label: 'Commissions vendeurs',
    icon: BadgeEuro,
    roles: ['responsable_partenaire', 'responsable_backoffice', 'manager', 'superviseur'],
    financeOpenOnly: true,
    financeGate: (a) => a.vendorCommissions === 'network' || a.vendorCommissions === 'team',
  },
  {
    to: '/mes-commissions',
    label: 'Mes commissions',
    icon: BadgeEuro,
    roles: ['vendeur'],
    financeOpenOnly: true,
    financeGate: (a) => a.vendorCommissions === 'own',
  },
  {
    to: '/configuration',
    label: 'Configuration',
    icon: Settings,
  },
];

interface MobileTab {
  to: string;
  label: string;
  icon: LucideIcon;
  roles?: UserRole[];
}

const MOBILE_TABS: MobileTab[] = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { to: '/leads', label: 'Fiches', icon: Handshake },
  // Center "+" button is handled separately
  {
    to: '/users',
    label: 'Gestion',
    icon: Shield,
    roles: ['admin', 'responsable_partenaire', 'responsable_backoffice', 'manager', 'superviseur'],
  },
];

/**
 * Session PIN (tablette partagée) : la navigation se réduit à ce que la
 * décision de réunion autorise — ses fiches du jour, la création, le
 * classement du magasin (spec §5). L'API refuse le reste de toute façon
 * (`PinSessionGuard`) ; ici on évite simplement de proposer des portes closes.
 */
const PIN_SIDEBAR_NAV: NavItem[] = [
  { to: '/leads', label: 'Mes fiches du jour', icon: Handshake },
  { to: '/classement', label: 'Classement', icon: Trophy },
];
const PIN_MOBILE_TABS: MobileTab[] = [
  { to: '/leads', label: 'Fiches', icon: Handshake },
  { to: '/classement', label: 'Classement', icon: Trophy },
];
const PIN_ALLOWED_PATHS = ['/leads', '/classement'];

function isPinAllowedPath(pathname: string): boolean {
  return PIN_ALLOWED_PATHS.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

function visibleSidebar(role: UserRole | undefined, isAggregator: boolean): NavItem[] {
  if (!role) return SIDEBAR_NAV.slice(0, 2);
  return SIDEBAR_NAV.filter((item) => {
    if (!item.roles || item.roles.includes(role)) return true;
    return item.aggregatorOnly === true && isAggregator;
  });
}

function visibleMobileTabs(role: UserRole | undefined): MobileTab[] {
  if (!role) return MOBILE_TABS.slice(0, 2);
  return MOBILE_TABS.filter((item) => !item.roles || item.roles.includes(role));
}

/** Routes affichées en pleine largeur (jusqu'à 1 400 px) sur ordinateur. */
const WIDE_ROUTES = ['/configuration', '/finance'];

// ── Shell ────────────────────────────────────────────────────────────

export function AppShell() {
  // user from Clerk used to be the email source ; remplacé par me.data.email
  // qui suit l'imperso. On garde useUser() au cas où d'autres composants
  // l'attendent (bundle déjà chargé pour Clerk).
  useUser();
  const { signOut } = useClerk();
  const { location } = useRouterState();
  const navigate = useNavigate();
  const me = useApiQuery<User>(['me'], '/users/me');
  // `/partners` renvoie toujours son propre réseau : on y lit le flag plutôt
  // que d'inventer un rôle. Requête déjà en cache sur la plupart des écrans.
  const partners = useApiQuery<Partner[]>(['partners'], '/partners');
  const myPartnerRow = partners.data?.find((p) => p.id === me.data?.partnerId);
  const isAggregator =
    me.data?.role === 'responsable_partenaire' && myPartnerRow?.isAggregator === true;
  // Session PIN : `authLevel` vient de l'API (source de vérité) ; le flag
  // local ne sert qu'à ne pas afficher une seconde la navigation complète
  // pendant que `/users/me` charge.
  const pinSession = me.data ? me.data.authLevel === 'pin' : isActingAs();
  const myPartnerPinEnabled = isRetail(myPartnerRow);
  // « Changer d'utilisateur » : en session PIN toujours ; sinon au superviseur
  // OU au manager (AMS : un manager par boutique, 13/10/2026) d'un réseau de
  // boutiques — c'est lui qui ouvre la tablette et déclenche la 1re bascule.
  // Bandeau magasin : tout compte qu'on affecte à un magasin (manager,
  // superviseur, vendeur — AMS n'a que des managers) et la session PIN. Le
  // wizard exige un choix quand plusieurs magasins sont ouverts : le bandeau
  // doit donc être là pour chacun d'eux. Le réseau est lu dans le cache des
  // magasins de l'appareil (`retail`), pas ici — il s'affiche hors ligne.
  const showStoreBanner = pinSession || isStoreAssignableRole(me.data?.role);
  const canSwitchUser =
    pinSession ||
    ((me.data?.role === 'superviseur' || me.data?.role === 'manager') && myPartnerPinEnabled);
  const [switchOpen, setSwitchOpen] = useState(false);
  const sidebarNav = pinSession
    ? PIN_SIDEBAR_NAV
    : visibleSidebar(me.data?.role, isAggregator === true);
  const mobileTabs = pinSession ? PIN_MOBILE_TABS : visibleMobileTabs(me.data?.role);

  // Filet de sécurité : si l'API refuse l'acteur PIN (réseau qui a désactivé
  // le PIN, vendeur archivé ou rattaché ailleurs entre-temps), `/users/me`
  // répond 403 et la tablette resterait coincée sur un écran vide. On
  // retombe sur la session du superviseur, qui rouvrira la bascule.
  const meStatus = (me.error as { status?: number } | null)?.status;
  useEffect(() => {
    if (me.isError && meStatus === 403 && isActingAs()) {
      clearActingAs();
      window.location.replace('/');
    }
  }, [me.isError, meStatus]);

  // Garde de route UX : en session PIN, toute adresse hors périmètre renvoie
  // aux fiches. Un favori sur /dashboard, un retour arrière — rien ne doit
  // afficher un écran que l'API refusera de remplir.
  useEffect(() => {
    if (pinSession && !isPinAllowedPath(location.pathname)) {
      void navigate({ to: '/leads', replace: true });
    }
  }, [pinSession, location.pathname, navigate]);
  // Le bouton « + » est centré par la MISE EN PAGE (deux moitiés `flex-1`
  // de part et d'autre), pas par le comptage des onglets : depuis le retrait
  // de l'onglet Équipes il y en a un nombre impair, et un `justify-around`
  // sur toute la barre poussait le « + » à droite du centre. L'onglet en trop
  // va à gauche, le pouce droit garde le « + » au milieu.
  const fabIndex = Math.ceil(mobileTabs.length / 2);
  const [loggingOut, setLoggingOut] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [impersonatePickerOpen, setImpersonatePickerOpen] = useState(false);

  const userInitials = me.data ? initialsFrom(me.data.firstName, me.data.lastName) : '?';
  // Le Fil : l'anneau sur l'avatar est le SEUL signal à l'ouverture (règle
  // fondatrice — un prospect peut voir l'écran). Dexie d'abord : la table
  // `stories` n'est peuplée que si l'API a ouvert le Fil à ce rôle (et vidée
  // sur 403), donc son contenu vaut preuve hors ligne — le flag TanStack
  // Query n'est qu'un complément quand le cache est encore vide.
  const flags = useFeatureFlags();
  const cachedStories = useStoriesCount();
  const unseenStories = useUnseenStoriesCount();
  // Le Fil appartient au compte, pas à la tablette : rien n'en transparaît en
  // session PIN (le cache Dexie, lui, reste intact pour le superviseur).
  const storiesOn =
    !pinSession && (cachedStories > 0 || storiesEnabledFor(flags.storiesVisibility, me.data?.role));
  const storyRing = storiesOn && unseenStories > 0;
  const financeOpen = flags.financeVisibility === 'everyone';
  const financeAccess = useFinanceAccess();
  const navItems = sidebarNav.filter(
    (item) =>
      (!item.retailOnly || me.data?.role === 'admin' || myPartnerPinEnabled) &&
      (!item.financeOpenOnly || financeOpen) &&
      (!item.financeGate || (!pinSession && item.financeGate(financeAccess))),
  );
  // Entrées rémunération et Magasins du menu mobile (sur desktop : la sidebar).
  const mobileExtraItems = pinSession
    ? []
    : navItems.filter((item) => item.financeGate !== undefined || item.retailOnly === true);
  const myPartner = useLiveQuery(
    () => (me.data?.partnerId ? db.partners.get(me.data.partnerId) : undefined),
    [me.data?.partnerId],
  );
  const myMatricule = me.data?.matricule ?? null;

  // Identifie le user pour PostHog dès que `me` est résolu. ID = userId
  // Doorly stable (pas l'email) → pas de PII chez PostHog. Tags `role` /
  // `partnerId` sont des propriétés non-PII utiles pour les breakdowns côté
  // analytics.
  const meId = me.data?.id;
  const meRole = me.data?.role;
  const mePartnerId = me.data?.partnerId;
  useEffect(() => {
    if (meId && meRole && mePartnerId) {
      identifyAnalytics(meId, { role: meRole, partnerId: mePartnerId });
    }
  }, [meId, meRole, mePartnerId]);
  const isStandaloneMode =
    typeof window !== 'undefined' &&
    (window.matchMedia('(display-mode: standalone)').matches ||
      (navigator as { standalone?: boolean }).standalone === true);

  if (loggingOut) {
    return (
      <div className="min-h-dvh grid place-items-center bg-bg">
        <div className="text-center space-y-4">
          <DoorlyLogo variant="icon" size={80} className="mx-auto animate-pulse" />
          <p className="text-ink-mute text-body font-medium">Déconnexion en cours…</p>
        </div>
      </div>
    );
  }

  if (isFullscreenPath(location.pathname)) {
    return (
      <div className="min-h-dvh bg-bg text-ink flex flex-col">
        <ImpersonationBanner />
        <TestModeBanner />
        {showStoreBanner && <StoreBanner />}
        <div className="flex-1">
          <Outlet />
        </div>
        {switchOpen && <SwitchUserModal open onClose={() => setSwitchOpen(false)} />}
      </div>
    );
  }

  // L'ImpersonationBanner est sticky top-0 z-50 (~36 px). On décale les
  // autres éléments sticky (sidebar, headers) en dessous via `top-9` quand
  // une session imperso est active — sinon ils se chevauchent et la
  // sidebar dépasse en bas, cachant le pied (version, matricule).
  const impersonating = isImpersonating();
  const stickyTop = impersonating ? 'top-9' : 'top-0';
  const sidebarHeight = impersonating ? 'h-[calc(100dvh-2.25rem)]' : 'h-dvh';

  return (
    <div className="min-h-dvh bg-bg text-ink flex flex-col">
      <ImpersonationBanner />
      <TestModeBanner />
      <div className="flex-1 flex">
        <InstallPWAPrompt />

        {/* ── Desktop sidebar — sticky full-viewport on desktop ─ */}
        <aside
          className={cn(
            'hidden md:flex md:flex-col w-60 border-r border-hair bg-surface sticky self-start overflow-y-auto',
            stickyTop,
            sidebarHeight,
          )}
        >
          <div className="flex items-center justify-center h-12 border-b border-hair px-4">
            <DoorlyLogo variant="full" size={32} />
          </div>
          <nav className="flex-1 p-2 space-y-0.5">
            {navItems.map((item) => {
              const active = location.pathname.startsWith(item.to);
              const Icon = item.icon;
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  className={cn(
                    'flex items-center gap-3 px-3 py-2 text-body transition-colors',
                    active
                      ? 'bg-brand text-brand-ink rounded-[var(--radius-sm)]'
                      : 'text-ink-soft hover:bg-surface-alt rounded-[var(--radius-sm)]',
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </Link>
              );
            })}
          </nav>
          <div className="sticky bottom-0 border-t border-hair bg-surface">
            {/* En mode standalone (PWA installée), InstallAppButton retourne
              null mais le wrapper p-2 + border-t laissait une bande vide
              séparée. On skip entièrement le wrapper quand on est
              déjà installé. */}
            {!isStandaloneMode && (
              <div className="p-2 border-b border-hair">
                <InstallAppButton />
              </div>
            )}
            {/* Tablette partagée : la bascule vit là où on lit qui est connecté.
              Pas de bandeau — l'identité affichée ici suffit, et un bandeau
              évoquerait l'imperso admin alors que le vendeur est chez lui. */}
            {canSwitchUser && (
              <button
                type="button"
                onClick={() => setSwitchOpen(true)}
                className="mx-2 mt-2 flex w-[calc(100%-1rem)] items-center justify-center gap-2 rounded-[var(--radius-sm)] bg-brand-soft px-3 py-2.5 text-body font-bold text-brand hover:brightness-95"
              >
                <Users className="h-4 w-4" />
                Changer d'utilisateur
              </button>
            )}
            <div className="px-3 py-2.5 flex items-start gap-2.5">
              <Avatar initials={userInitials} size={32} />
              <div className="min-w-0 flex-1">
                <div className="text-body font-semibold text-ink truncate">
                  {me.data?.firstName} {me.data?.lastName}
                </div>
                {myMatricule && (
                  <div className="text-micro font-semibold text-ink-soft truncate">
                    {myMatricule}
                  </div>
                )}
                {(me.data || myPartner) && (
                  // Rôle + partenaire sur la même ligne — plus compact dans la
                  // sidebar et l'info reste lisible.
                  <div className="text-micro text-ink-mute truncate flex items-center gap-1.5 mt-0.5">
                    {me.data && <span>{me.data.role}</span>}
                    {me.data && myPartner && <span className="opacity-60">·</span>}
                    {myPartner && (
                      <span className="font-semibold text-ink-soft truncate">{myPartner.name}</span>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="border-t border-hair">
              <AppVersion />
            </div>
          </div>
        </aside>

        {/* ── Main content ────────────────────────────────── */}
        <main className="flex-1 flex flex-col min-w-0">
          {/* Mobile header */}
          <header
            className={cn(
              'md:hidden sticky z-10 bg-surface border-b border-hair px-5 h-14 flex items-center justify-between gap-3',
              stickyTop,
            )}
          >
            <h1 className="text-title font-extrabold tracking-tight text-ink">
              {pageTitleFromPath(location.pathname)}
            </h1>
            <div className="flex items-center gap-2">
              <SyncStatusBadge />
              <PendingConsentBell />
              <button
                type="button"
                onClick={() => setMenuOpen((v) => !v)}
                className="rounded-full p-0.5 tap-target flex items-center justify-center"
                aria-label="Menu"
                style={{ boxShadow: menuOpen ? '0 0 0 2px var(--color-brand)' : 'none' }}
              >
                <Avatar initials={userInitials} size={40} ring={storyRing} />
              </button>
            </div>
          </header>

          {/* Desktop header */}
          <header
            className={cn(
              'hidden md:flex sticky z-10 bg-surface border-b border-hair px-6 h-12 items-center justify-end gap-3',
              stickyTop,
            )}
          >
            <SyncStatusBadge />
            <PendingConsentBell />
            <button type="button" onClick={() => setMenuOpen((v) => !v)} className="rounded-full">
              <Avatar initials={userInitials} size={32} ring={storyRing} />
            </button>
          </header>

          {/* Avatar dropdown menu */}
          {menuOpen && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setMenuOpen(false)} />
              <div
                className="fixed z-50 w-60 bg-surface border border-hair overflow-hidden"
                style={{
                  top: 56,
                  right: 16,
                  borderRadius: 'var(--radius-card)',
                  boxShadow: 'var(--shadow-elevated)',
                }}
              >
                <div className="p-3.5 flex items-center gap-3 border-b border-divider">
                  <Avatar initials={userInitials} size={38} />
                  <div className="min-w-0">
                    <div className="text-body font-bold text-ink truncate">
                      {me.data?.firstName} {me.data?.lastName}
                    </div>
                    {myMatricule && me.data && (
                      <div className="text-micro text-ink-mute truncate flex items-center gap-1.5">
                        <span className="font-semibold text-ink-soft">{myMatricule}</span>
                        <span className="opacity-60">·</span>
                        <span>{me.data.role}</span>
                      </div>
                    )}
                  </div>
                </div>
                {/* Le Fil : première ligne du menu quand le flag l'ouvre à ce rôle.
                  C'est ici que l'anneau se résout — l'avatar ouvre déjà ce
                  menu, on ne lui ajoute pas un second geste. */}
                {storiesOn && (
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      void navigate({ to: '/nouveautes' });
                    }}
                    className="w-full px-3.5 py-3 flex items-center gap-2.5 text-body font-semibold text-ink hover:bg-surface-alt border-b border-divider"
                  >
                    <Sparkles className="h-4 w-4 text-brand" />
                    Nouveautés
                    {unseenStories > 0 && (
                      <span className="ml-auto inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-brand text-brand-ink text-micro font-extrabold tabular-nums">
                        {unseenStories > 9 ? '9+' : unseenStories}
                      </span>
                    )}
                  </button>
                )}
                {/* Tablette partagée : la bascule de vendeur par code PIN. En
                  session PIN c'est la SEULE action du menu avec la version —
                  le reste (configuration, installation, déconnexion) relève
                  du superviseur ou d'une connexion complète. */}
                {canSwitchUser && (
                  <button
                    type="button"
                    onClick={() => {
                      setMenuOpen(false);
                      setSwitchOpen(true);
                    }}
                    className="w-full px-3.5 py-3 flex items-center gap-2.5 text-body font-bold text-brand bg-brand-soft/60 hover:bg-brand-soft border-b border-divider"
                  >
                    <Users className="h-4 w-4" />
                    Changer d'utilisateur
                  </button>
                )}
                {/* Tout ce qui suit relève d'une session complète — un seul
                  garde-fou pour le bloc, pas un par entrée. */}
                {!pinSession && (
                  <>
                    {/* Configuration : tout le monde y a accès, le contenu est filtré
                  côté ConfigurationPage par rôle (préférences perso pour
                  tous, réseau pour RP, admin pour le reste). */}
                    <button
                      type="button"
                      onClick={() => {
                        setMenuOpen(false);
                        void navigate({ to: '/configuration' });
                      }}
                      className="w-full px-3.5 py-3 flex items-center gap-2.5 text-body font-semibold text-ink hover:bg-surface-alt border-b border-divider"
                    >
                      <Settings className="h-4 w-4" />
                      Configuration
                    </button>
                    {/* Rémunération (drapeau ouvert, réglages du RP) : entrées
                      mobiles — sur desktop elles vivent dans la sidebar. */}
                    {mobileExtraItems.map((item) => {
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.to}
                          type="button"
                          onClick={() => {
                            setMenuOpen(false);
                            void navigate({ to: item.to });
                          }}
                          className="w-full px-3.5 py-3 flex items-center gap-2.5 text-body font-semibold text-ink hover:bg-surface-alt border-b border-divider md:hidden"
                        >
                          <Icon className="h-4 w-4" />
                          {item.label}
                        </button>
                      );
                    })}
                    {/* Admin-only shortcuts that only live in the desktop sidebar */}
                    {me.data?.role === 'admin' && (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            setMenuOpen(false);
                            setImpersonatePickerOpen(true);
                          }}
                          className="w-full px-3.5 py-3 flex items-center gap-2.5 text-body font-semibold text-ink hover:bg-surface-alt border-b border-divider"
                        >
                          <UserCog className="h-4 w-4" />
                          Imperso utilisateur
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setMenuOpen(false);
                            void navigate({ to: '/finance' });
                          }}
                          className="w-full px-3.5 py-3 flex items-center gap-2.5 text-body font-semibold text-ink hover:bg-surface-alt border-b border-divider md:hidden"
                        >
                          <Wallet className="h-4 w-4" />
                          Finance
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setMenuOpen(false);
                            void navigate({ to: '/partners' });
                          }}
                          className="w-full px-3.5 py-3 flex items-center gap-2.5 text-body font-semibold text-ink hover:bg-surface-alt border-b border-divider md:hidden"
                        >
                          <Building2 className="h-4 w-4" />
                          Partenaires
                        </button>
                        {/* Tickets : entrée admin mobile — sur desktop l'entrée
                      vit dans la sidebar (roles: ['admin']). */}
                        <button
                          type="button"
                          onClick={() => {
                            setMenuOpen(false);
                            void navigate({ to: '/tickets' });
                          }}
                          className="w-full px-3.5 py-3 flex items-center gap-2.5 text-body font-semibold text-ink hover:bg-surface-alt border-b border-divider md:hidden"
                        >
                          <MessageCircleQuestion className="h-4 w-4" />
                          Tickets
                        </button>
                      </>
                    )}
                    {!isStandaloneMode && (
                      <button
                        type="button"
                        onClick={() => {
                          setMenuOpen(false);
                          if (canInstallNatively()) {
                            void triggerInstall();
                          } else {
                            localStorage.removeItem('doorly:pwa-install-dismissed');
                            window.location.reload();
                          }
                        }}
                        className="w-full px-3.5 py-3 flex items-center gap-2.5 text-body font-semibold text-ink hover:bg-surface-alt border-b border-divider"
                      >
                        <Download className="h-4 w-4" />
                        Installer l'app
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setMenuOpen(false);
                        handleSignOut(signOut, setLoggingOut);
                      }}
                      className="w-full px-3.5 py-3 flex items-center gap-2.5 text-body font-semibold text-danger border-b border-divider"
                    >
                      <LogOut className="h-4 w-4" />
                      Se déconnecter
                    </button>
                  </>
                )}
                {/* Vérifier la mise à jour reste accessible en PIN : c'est sur
                  les tablettes qu'on en a le plus besoin. */}
                <UpdateCheckButton onClose={() => setMenuOpen(false)} />
                <AppVersion />
              </div>
            </>
          )}

          {/* Desktop FAB — always visible */}
          <Link
            to="/leads/new"
            className="hidden md:flex fixed bottom-6 right-6 z-30 items-center gap-2 px-5 h-12 rounded-full bg-brand text-brand-ink font-bold text-body shadow-lg hover:brightness-110 transition-all"
            style={{ boxShadow: '0 8px 24px rgba(42, 46, 212, 0.35)' }}
          >
            <Plus className="h-5 w-5" strokeWidth={2.4} />
            Nouvelle fiche
          </Link>

          <div className="flex-1 pb-24 md:pb-0">
            {/* Écrans de pilotage (configuration, finance) : tableaux et
                panneaux côte à côte — ils profitent de la largeur d'un
                écran d'ordinateur. Le reste garde la colonne de lecture. */}
            {showStoreBanner && <StoreBanner />}
            <div
              className={cn(
                'mx-auto p-4 md:p-6',
                WIDE_ROUTES.some((r) => location.pathname.startsWith(r))
                  ? 'max-w-[1400px]'
                  : 'max-w-5xl',
              )}
            >
              <Outlet />
            </div>
          </div>

          {/* ── Mobile bottom nav with raised + button ──── */}
          <nav className="md:hidden fixed bottom-0 inset-x-0 z-10 bg-surface/95 backdrop-blur-xl border-t border-hair pb-[env(safe-area-inset-bottom)]">
            <div className="flex items-end px-1 pt-2 pb-2">
              <div className="flex-1 flex items-end justify-around">
                {mobileTabs.slice(0, fabIndex).map((tab) => (
                  <MobileTabButton
                    key={tab.to}
                    tab={tab}
                    active={location.pathname.startsWith(tab.to)}
                  />
                ))}
              </div>

              {/* Central FAB: Nouveau lead — outer button is the real tap target,
                the inner circle is the visual. Keeps margin forgiving for
                off-center taps on mobile. */}
              <button
                type="button"
                onClick={() => void navigate({ to: '/leads/new' })}
                className="flex flex-col items-center justify-center -mt-5 px-4 py-1 min-h-[56px] min-w-[72px]"
                aria-label="Nouvelle fiche"
              >
                <div
                  className="w-14 h-14 rounded-full bg-brand text-brand-ink flex items-center justify-center pointer-events-none"
                  style={{
                    boxShadow:
                      '0 8px 20px rgba(45, 91, 255, 0.35), 0 2px 4px rgba(45, 91, 255, 0.28)',
                  }}
                >
                  <Plus className="h-7 w-7" strokeWidth={2.4} />
                </div>
                <span className="text-micro font-semibold text-ink-mute mt-1 pointer-events-none">
                  Nouveau
                </span>
              </button>

              <div className="flex-1 flex items-end justify-around">
                {mobileTabs.slice(fabIndex).map((tab) => (
                  <MobileTabButton
                    key={tab.to}
                    tab={tab}
                    active={location.pathname.startsWith(tab.to)}
                  />
                ))}
              </div>
            </div>
          </nav>
        </main>
      </div>
      <ImpersonationPicker
        open={impersonatePickerOpen}
        onClose={() => setImpersonatePickerOpen(false)}
      />
      {/* Montée à la demande : ses live queries Dexie n'ont pas à tourner
          pour les rôles qui ne l'ouvriront jamais. */}
      {switchOpen && <SwitchUserModal open onClose={() => setSwitchOpen(false)} />}
    </div>
  );
}

function MobileTabButton({ tab, active }: { tab: MobileTab; active: boolean }) {
  const Icon = tab.icon;
  return (
    <Link
      to={tab.to}
      className={cn(
        'flex flex-col items-center justify-center gap-1 min-h-[52px] min-w-[52px] flex-1 py-1 transition-colors',
        active ? 'text-brand' : 'text-ink-mute',
      )}
    >
      <Icon className="h-6 w-6 pointer-events-none" strokeWidth={active ? 2.1 : 1.75} />
      <span className={cn('text-micro pointer-events-none', active ? 'font-bold' : 'font-medium')}>
        {tab.label}
      </span>
    </Link>
  );
}

function pageTitleFromPath(path: string): string {
  if (path.startsWith('/nouveautes')) return 'Nouveautés';
  if (path.startsWith('/dashboard')) return 'Dashboard';
  if (path.startsWith('/classement')) return 'Classement';
  if (path.startsWith('/leads/new')) return 'Nouvelle fiche';
  if (path.startsWith('/leads/')) return 'Fiche';
  if (path.startsWith('/leads')) return 'Fiches';
  if (path.startsWith('/tickets')) return 'Tickets';
  if (path.startsWith('/users/invite')) return 'Inviter';
  if (path.startsWith('/users')) return 'Utilisateurs';
  if (path.startsWith('/partners/new')) return 'Nouveau partenaire';
  if (path.startsWith('/partners')) return 'Partenaires';
  if (path.startsWith('/configuration')) return 'Configuration';
  if (path.startsWith('/finance')) return 'Finance';
  if (path.startsWith('/magasins')) return 'Magasins';
  if (path.startsWith('/ma-remuneration')) return 'Ma rémunération';
  if (path.startsWith('/commissions')) return 'Commissions vendeurs';
  if (path.startsWith('/mes-commissions')) return 'Mes commissions';
  return 'Doorly';
}

/**
 * "Check for updates" button in the avatar menu. Wraps the SW-level check so
 * a vendor can manually confirm they're on the latest bundle. The auto-reload
 * logic in main.tsx takes over when a new SW is detected — this button just
 * surfaces the result to the user.
 */
function UpdateCheckButton({ onClose }: { onClose: () => void }) {
  const [checking, setChecking] = useState(false);

  async function onClick() {
    setChecking(true);
    try {
      const result = await checkForUpdates();
      if (result === 'updating') {
        toast.info('Mise à jour détectée — rechargement…');
      } else if (result === 'no-sw') {
        toast.info('Service worker inactif — vérification dispo seulement en prod.');
      } else if (result === 'unsupported') {
        toast.info('Votre navigateur ne supporte pas la vérification.');
      } else {
        toast.success('Vous êtes à jour ✓');
      }
    } finally {
      setChecking(false);
      onClose();
    }
  }

  return (
    <button
      type="button"
      onClick={() => void onClick()}
      disabled={checking}
      className="w-full px-3.5 py-3 flex items-center gap-2.5 text-body font-semibold text-ink hover:bg-surface-alt border-b border-divider disabled:opacity-60"
    >
      <RefreshCcw className={cn('h-4 w-4', checking && 'animate-spin')} />
      {checking ? 'Vérification…' : 'Vérifier la mise à jour'}
    </button>
  );
}
